import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  fetchProductsList,
  fetchCategoriesAndSubcategories
} from '../../redux/slices/productsSlice';

const CouponForm = ({
  coupon = null,
  onSave,
  onCancel,
  isOpen = false
}) => {
  const dispatch = useDispatch();
  const { data: products, categories } = useSelector(state => state.products);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    coupon_type: 'percentage',
    discount_value: '',
    start_date: '',
    end_date: '',
    usage_type: 'unlimited',
    max_uses: 1,
    max_uses_per_user: 1,
    minimum_order_amount: '0.00',
    maximum_order_amount: '',
    applicability: 'all_products',
    applicable_categories: [],
    applicable_products: [],
    status: 'active',
    is_stackable: false
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // load products and categories once
  useEffect(() => {
    dispatch(fetchProductsList());
    dispatch(fetchCategoriesAndSubcategories());
  }, [dispatch]);

  // populate form when editing
  useEffect(() => {
    if (!coupon) return;
    setFormData({
      code: coupon.code || '',
      name: coupon.name || '',
      description: coupon.description || '',
      coupon_type: coupon.coupon_type || 'percentage',
      discount_value: coupon.discount_value || '',
      start_date: coupon.start_date?.split('T')[0] || '',
      end_date: coupon.end_date?.split('T')[0] || '',
      usage_type: coupon.usage_type || 'unlimited',
      max_uses: coupon.max_uses || 1,
      max_uses_per_user: coupon.max_uses_per_user || 1,
      minimum_order_amount: coupon.minimum_order_amount || '0.00',
      maximum_order_amount: coupon.maximum_order_amount || '',
      applicability: coupon.applicability || 'all_products',
      applicable_categories: coupon.applicable_categories || [],
      applicable_products: coupon.applicable_products || [],
      status: coupon.status || 'active',
      is_stackable: coupon.is_stackable || false
    });
  }, [coupon]);

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(f => ({
      ...f,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleMultiSelectChange = e => {
    const { name, options } = e.target;
    const vals = Array.from(options).filter(o => o.selected).map(o => o.value);
    setFormData(f => ({ ...f, [name]: vals }));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.code.trim()) errs.code = 'Coupon code is required';
    if (!formData.name.trim()) errs.name = 'Coupon name is required';
    if (!formData.discount_value || +formData.discount_value <= 0)
      errs.discount_value = 'Discount value must be greater than 0';
    if (formData.coupon_type === 'percentage' && +formData.discount_value > 100)
      errs.discount_value = 'Percentage cannot exceed 100%';
    if (!formData.start_date) errs.start_date = 'Start date is required';
    if (!formData.end_date) errs.end_date = 'End date is required';
    if (
      formData.start_date &&
      formData.end_date &&
      formData.start_date >= formData.end_date
    )
      errs.end_date = 'End date must be after start date';
    if (
      formData.usage_type === 'limited' &&
      (!formData.max_uses || +formData.max_uses <= 0)
    )
      errs.max_uses = 'Max uses is required for limited usage';
    if (
      formData.applicability === 'specific_categories' &&
      formData.applicable_categories.length === 0
    )
      errs.applicable_categories = 'Select at least one category';
    if (
      formData.applicability === 'specific_products' &&
      formData.applicable_products.length === 0
    )
      errs.applicable_products = 'Select at least one product';

    setErrors(errs);
    return !Object.keys(errs).length;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      await onSave(formData);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-96 overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {coupon ? 'Edit Coupon' : 'Create New Coupon'}
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coupon Code *
              </label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 font-mono ${
                  errors.code ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="SUMMER20"
              />
              {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coupon Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Summer Sale 20% Off"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Describe this coupon..."
              />
            </div>

            {/* Discount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Coupon Type *
              </label>
              <select
                name="coupon_type"
                value={formData.coupon_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="percentage">Percentage Discount</option>
                <option value="fixed_amount">Fixed Amount Discount</option>
                <option value="free_shipping">Free Shipping</option>
                <option value="buy_x_get_y">Buy X Get Y Free</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount Value *
              </label>
              <input
                type="number"
                name="discount_value"
                value={formData.discount_value}
                onChange={handleInputChange}
                step="0.01"
                min="0.01"
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.discount_value ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder={formData.coupon_type === 'percentage' ? '20' : '10.00'}
              />
              {errors.discount_value && (
                <p className="text-red-500 text-xs mt-1">{errors.discount_value}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {formData.coupon_type === 'percentage'
                  ? 'Percentage (1-100)'
                  : 'Fixed amount in dollars'}
              </p>
            </div>

            {/* Dates */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date *
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.start_date ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.start_date && (
                <p className="text-red-500 text-xs mt-1">{errors.start_date}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date *
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  errors.end_date ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.end_date && (
                <p className="text-red-500 text-xs mt-1">{errors.end_date}</p>
              )}
            </div>

            {/* Usage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Usage Type
              </label>
              <select
                name="usage_type"
                value={formData.usage_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="unlimited">Unlimited</option>
                <option value="limited">Limited Uses</option>
                <option value="single_use">Single Use</option>
              </select>
            </div>
            {formData.usage_type === 'limited' && (
              <div>…
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Uses *
                </label>
                <input
                  type="number"
                  name="max_uses"
                  value={formData.max_uses}
                  onChange={handleInputChange}
                  min="1"
                  className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                    errors.max_uses ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.max_uses && (
                  <p className="text-red-500 text-xs mt-1">{errors.max_uses}</p>
                )}
              </div>
            )}

            {/* Applicability */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applicability *
              </label>
              <select
                name="applicability"
                value={formData.applicability}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="all_products">All Products</option>
                {/* <option value="specific_products">Specific Products</option> */}
                <option value="specific_categories">Specific Categories</option>
                <option value="first_time_customers">
                  First Time Customers Only
                </option>
              </select>
            </div>

            {formData.applicability === 'specific_categories' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Categories *
                </label>
                <select
                  multiple
                  name="applicable_categories"
                  value={formData.applicable_categories}
                  onChange={handleMultiSelectChange}
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
                {errors.applicable_categories && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.applicable_categories}
                  </p>
                )}
              </div>
            )}

            {formData.applicability === 'specific_products' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Products *
                </label>
                <select
                  multiple
                  name="applicable_products"
                  value={formData.applicable_products}
                  onChange={handleMultiSelectChange}
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {errors.applicable_products && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.applicable_products}
                  </p>
                )}
              </div>
            )}

            {/* Status & Options */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_stackable"
                checked={formData.is_stackable}
                onChange={handleInputChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Allow stacking with other coupons
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  {coupon ? 'Update Coupon' : 'Create Coupon'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CouponForm;